import { MediaPickerField } from "@/components/cms-puck/fields/MediaPickerField";
import { PuckRichTextEditor } from "@/components/cms-puck/fields/PuckRichTextEditor";
import { SelectionDisplayField } from "@/components/cms-puck/fields/SelectionDisplayField";
import { SelectionPickerField } from "@/components/cms-puck/fields/SelectionPickerField";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  createEmptySelectionCategory,
  DEFAULT_SELECTION_VISIBILITY,
  selectionCategoryPlainTitle,
  type SelectionCategory,
  type SelectionItem,
  type SelectionSectionsMode,
  type SelectionVisibility,
} from "@/lib/cms-puck/blocks/selection";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { usePuck } from "@measured/puck";
import type { ComponentData } from "@measured/puck";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { useState } from "react";

function newCategoryId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `cat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

type BlockProps = {
  id?: string;
  mode?: SelectionSectionsMode;
  categoryColumns?: 2 | 3 | 4;
  categories?: SelectionCategory[];
  items?: SelectionItem[];
  columns?: 2 | 3 | 4;
  visibility?: SelectionVisibility;
};

/**
 * Éditeur du bloc `SelectionSections` : lit/écrit les props du bloc sélectionné
 * via `usePuck` (nécessaire pour les vignettes imbriquées gérées par
 * `MediaPickerHost` avec `__selCatThumb__:<id>`).
 */
export function SelectionSectionsField() {
  const { selectedItem, dispatch, getSelectorForId } = usePuck();
  const props = (selectedItem?.props ?? {}) as BlockProps;
  const blockId = props.id;
  const [openId, setOpenId] = useState<string | null>(
    props.categories?.[0]?.id ?? null,
  );

  if (!selectedItem || selectedItem.type !== "SelectionSections" || !blockId) {
    return (
      <p className="p-1 text-xs text-slate-500">
        {cmsNew(
          "cms.new.hint.selectionSections.select",
          "Sélectionnez le bloc Sélection — catégories.",
        )}
      </p>
    );
  }

  const mode: SelectionSectionsMode =
    props.mode === "global" ? "global" : "categorized";
  const categories = props.categories ?? [];
  const items = props.items ?? [];
  const categoryColumns = props.categoryColumns ?? 4;
  const columns = props.columns ?? 4;
  const visibility = props.visibility ?? { ...DEFAULT_SELECTION_VISIBILITY };

  const patch = (partial: Partial<BlockProps>) => {
    const selector = getSelectorForId(blockId);
    if (!selector) return;
    const node = selectedItem as ComponentData;
    dispatch({
      type: "replace",
      destinationIndex: selector.index,
      destinationZone: selector.zone,
      data: {
        ...node,
        props: { ...node.props, ...partial },
      },
    });
  };

  const updateCategory = (id: string, partial: Partial<SelectionCategory>) => {
    patch({
      categories: categories.map((c) =>
        c.id === id ? { ...c, ...partial } : c,
      ),
    });
  };

  const moveCategory = (index: number, dir: -1 | 1) => {
    const next = [...categories];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    patch({ categories: next });
  };

  return (
    <div className="flex flex-col gap-3 p-1">
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-slate-600">
          {cmsNew("cms.new.auto.label.mode", "Mode")}
        </Label>
        <div className="flex gap-2">
          {(
            [
              ["categorized", "Par rubriques"],
              ["global", "Grille globale"],
            ] as const
          ).map(([m, label]) => (
            <Button
              key={m}
              type="button"
              size="sm"
              variant={mode === m ? "default" : "outline"}
              onClick={() => patch({ mode: m })}
            >
              {cmsNew(
                m === "categorized"
                  ? "cms.new.options.selection.mode.categorized"
                  : "cms.new.options.selection.mode.global",
                label,
              )}
            </Button>
          ))}
        </div>
      </div>

      {mode === "global" ? (
        <div className="flex flex-col gap-3">
          <SelectionPickerField
            value={items}
            onChange={(next) => patch({ items: next })}
          />
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-slate-600">
              {cmsNew("cms.new.auto.label.colonnes", "Colonnes")}
            </Label>
            <select
              className="h-8 rounded border border-slate-200 bg-white px-2 text-sm"
              value={columns}
              onChange={(e) =>
                patch({ columns: Number(e.target.value) as 2 | 3 | 4 })
              }
            >
              {[2, 3, 4].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <SelectionDisplayField
            value={visibility}
            onChange={(next) => patch({ visibility: next })}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-slate-600">
              {cmsNew(
                "cms.new.auto.label.colonnes_categories",
                "Colonnes (grille des rubriques)",
              )}
            </Label>
            <select
              className="h-8 rounded border border-slate-200 bg-white px-2 text-sm"
              value={categoryColumns}
              onChange={(e) =>
                patch({
                  categoryColumns: Number(e.target.value) as 2 | 3 | 4,
                })
              }
            >
              {[2, 3, 4].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-700">
              {cmsNew("cms.new.auto.label.rubriques", "Rubriques")} (
              {categories.length})
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                const cat = createEmptySelectionCategory(newCategoryId());
                patch({ categories: [...categories, cat] });
                setOpenId(cat.id);
              }}
            >
              <PlusIcon className="mr-1 size-3.5" />
              {cmsNew("cms.new.auto.action.add", "Ajouter")}
            </Button>
          </div>

          {categories.length === 0 ? (
            <p className="text-xs text-slate-500">
              {cmsNew(
                "cms.new.hint.selectionSections.empty",
                "Aucune rubrique. Ajoutez-en pour afficher une grille de catégories.",
              )}
            </p>
          ) : null}

          <ul className="flex flex-col gap-2">
            {categories.map((cat, index) => {
              const open = openId === cat.id;
              return (
                <li
                  key={cat.id}
                  className="rounded border border-slate-200 bg-slate-50/60"
                >
                  <div className="flex items-center gap-1 px-2 py-1.5">
                    <button
                      type="button"
                      className="min-w-0 flex-1 truncate text-left text-sm font-medium text-slate-800"
                      onClick={() => setOpenId(open ? null : cat.id)}
                    >
                      {selectionCategoryPlainTitle(cat.title) ||
                        cmsNew(
                          "cms.new.preview.selectionSections.category",
                          "Catégorie",
                        )}{" "}
                      <span className="font-normal text-slate-400">
                        ({cat.items?.length ?? 0})
                      </span>
                    </button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      disabled={index === 0}
                      onClick={() => moveCategory(index, -1)}
                    >
                      <ChevronUpIcon className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      disabled={index === categories.length - 1}
                      onClick={() => moveCategory(index, 1)}
                    >
                      <ChevronDownIcon className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-7 text-red-600"
                      onClick={() =>
                        patch({
                          categories: categories.filter((c) => c.id !== cat.id),
                        })
                      }
                    >
                      <Trash2Icon className="size-3.5" />
                    </Button>
                  </div>
                  {open ? (
                    <div className="flex flex-col gap-3 border-t border-slate-200 p-2">
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs text-slate-600">
                          {cmsNew("cms.new.auto.label.titre", "Titre")}
                        </Label>
                        <PuckRichTextEditor
                          value={cat.title ?? ""}
                          onChange={(html) =>
                            updateCategory(cat.id, { title: html })
                          }
                          placeholder={cmsNew(
                            "cms.new.fields.richText.placeholder",
                            "Écrivez votre texte…",
                          )}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs text-slate-600">
                          {cmsNew(
                            "cms.new.auto.label.description",
                            "Description",
                          )}
                        </Label>
                        <PuckRichTextEditor
                          value={cat.description ?? ""}
                          onChange={(html) =>
                            updateCategory(cat.id, { description: html })
                          }
                          placeholder={cmsNew(
                            "cms.new.fields.richText.placeholder",
                            "Écrivez votre texte…",
                          )}
                        />
                      </div>
                      <MediaPickerField
                        fieldName={`__selCatThumb__:${cat.id}`}
                        value={cat.thumbnailAttachmentId ?? undefined}
                        onChange={(attachmentId) =>
                          updateCategory(cat.id, {
                            thumbnailAttachmentId: attachmentId || null,
                          })
                        }
                        label={cmsNew(
                          "cms.new.auto.label.vignette",
                          "Vignette",
                        )}
                        allowedCatalogKinds={["image"]}
                      />
                      <SelectionPickerField
                        value={cat.items ?? []}
                        onChange={(next) =>
                          updateCategory(cat.id, { items: next })
                        }
                      />
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs text-slate-600">
                          {cmsNew(
                            "cms.new.auto.label.colonnes_oeuvres",
                            "Colonnes (œuvres)",
                          )}
                        </Label>
                        <select
                          className="h-8 rounded border border-slate-200 bg-white px-2 text-sm"
                          value={cat.columns ?? 4}
                          onChange={(e) =>
                            updateCategory(cat.id, {
                              columns: Number(e.target.value) as 2 | 3 | 4,
                            })
                          }
                        >
                          {[2, 3, 4].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                      <SelectionDisplayField
                        value={cat.visibility}
                        onChange={(next) =>
                          updateCategory(cat.id, { visibility: next })
                        }
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
